import type { CommentDrop, PhotoPick, PointPath, PointStep, SectionSelection } from "../components/PreviewFrame";

const POINT_LEVELS: ReadonlySet<string> = new Set(["section", "item", "text", "photo", "slot"]);

/** A Point to change path from the page, kept only when it is well formed. */
export function pointPath(value: unknown): PointPath | undefined {
  const candidate = value as { steps?: unknown; depth?: unknown } | null;
  if (typeof candidate !== "object" || candidate === null || !Array.isArray(candidate.steps) || !Number.isInteger(candidate.depth)) return undefined;
  const steps = candidate.steps.flatMap((step: unknown) => {
    const entry = step as { level?: unknown; label?: unknown } | null;
    return entry && typeof entry.level === "string" && POINT_LEVELS.has(entry.level) && typeof entry.label === "string"
      ? [{ level: entry.level as PointStep["level"], label: entry.label.slice(0, 80) }]
      : [];
  });
  const depth = candidate.depth as number;
  return steps.length === candidate.steps.length && depth >= 0 && depth < steps.length ? { steps, depth } : undefined;
}

/** A step's name as the owner reads it: words are named by their field, e.g. "Title". */
export function stepName(step: PointStep, fields: Record<string, string>): string {
  if (step.level === "text") return fields[step.label] ?? "Text";
  return step.label || (step.level === "section" ? "Section" : step.level === "item" ? "Item" : "Photo");
}

export type BridgeMode = "off" | "select" | "point";

export function bridgeMode({ mode, pointer, commenting, canEdit }: {
  mode: "simple" | "advanced";
  pointer: boolean;
  commenting: boolean;
  canEdit: boolean;
}): BridgeMode {
  if (!canEdit) return "off";
  if (mode === "advanced") return pointer && !commenting ? "select" : "off";
  return pointer ? "point" : "off";
}

export function dropAt(anchor: SectionSelection | PhotoPick, viewport: "phone" | "desktop"): CommentDrop {
  const isSelection = "rect" in anchor;
  const left = isSelection ? anchor.rect.left + anchor.rect.width / 2 : anchor.left;
  const top = isSelection ? anchor.rect.top + anchor.rect.height / 2 : anchor.bottom;
  return {
    nodeId: anchor.nodeId ?? "",
    ...(anchor.itemId ? { itemId: anchor.itemId } : {}),
    x: isSelection ? 0.5 : anchor.x,
    y: isSelection ? 0.5 : anchor.y,
    viewport,
    top,
    bottom: top,
    left,
    ...(anchor.scrollY !== undefined ? { scrollY: anchor.scrollY } : {}),
  };
}
